import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <img
        src="https://readdy.ai/api/search-image?query=Artistic%20stylized%20abstract%20fashion%20collage%20with%20overlapping%20pastel%20geometric%20shapes%20in%20warm%20terracotta%20sage%20green%20and%20cream%20tones%2C%20floating%20product%20silhouettes%20of%20handbags%20sunglasses%20and%20folded%20garments%20subtly%20integrated%2C%20soft%20gradient%20background%20with%20gentle%20light%20leaks%2C%20modern%20minimalist%20editorial%20design%2C%20clean%20sophisticated%20ecommerce%20aesthetic%2C%20no%20text%20no%20logos%20airy%20composition&width=1800&height=1000&seq=gtbuy-hero-v2b&orientation=landscape"
        alt="GTBuy Spreadsheet Shopping Agent Comparison - Artistic fashion collage with warm earth tones"
        title="GTBuy Spreadsheet - Shopping Agent Comparison Tool"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6 flex flex-col items-center text-center">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
          GTBuy Spreadsheet &mdash; Smart Shopping Agent Comparison
        </h1>
        <p className="mt-4 md:mt-6 text-sm md:text-base text-white/80 max-w-xl leading-relaxed">
          Compare prices, shipping rates, and trust scores across top shopping agents.
          Find the cheapest and most reliable agent for sneakers, fashion, electronics, and more.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-3 mt-6 md:mt-8">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-white px-8 py-3 rounded-md text-sm md:text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            gtbuy sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-white/90 text-foreground-900 px-8 py-3 rounded-md text-sm md:text-base font-semibold hover:bg-white transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            buy it now
          </a>
        </div>

        {/* Category Dropdown */}
        <div className="relative mt-6 md:mt-8" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 bg-white/95 backdrop-blur-sm text-foreground-900 px-5 py-3 rounded-md text-sm md:text-base font-medium hover:bg-white transition-all whitespace-nowrap cursor-pointer shadow-lg"
          >
            <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
            <span>Browse Categories</span>
            {dropdownOpen ? (
              <i className="ri-arrow-up-s-line w-5 h-5 flex items-center justify-center"></i>
            ) : (
              <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center"></i>
            )}
          </button>

          {dropdownOpen && (
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-white rounded-lg overflow-hidden z-20 shadow-xl">
              <div className="max-h-80 overflow-y-auto py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-foreground-400 ml-auto"></i>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}